import SidebarLayout from "@/components/feature/SidebarLayout";
import { useScrollReveal, getRevealClass } from "@/hooks/useScrollReveal";

const requestTypes = [
  "TV出演",
  "映画出演",
  "ラジオ出演",
  "YouTube出演",
  "CM出演",
  "モデル",
  "ナレーション",
  "イベントMC",
  "お祭り司会",
  "講演など",
];

export default function Entertainment() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <SidebarLayout breadcrumbs={[{ label: 'タレントマネジメント事業' }]}>

      {/* Hero */}
      <section className="px-4 md:px-6 lg:px-10 mb-10">
        <div className="relative w-full h-[320px] sm:h-[400px] md:h-[500px] lg:h-[580px] overflow-hidden rounded-2xl">
          <img
            src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/c2e216ef27f90d8b313d166de422ac50.webp"
            alt="タレントマネジメント事業"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/25" />
          {/* Center text */}
          <div ref={ref} className={`absolute inset-0 flex flex-col items-center justify-center z-10 ${getRevealClass(isVisible)}`}>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-wide"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              Talent Management
            </h1>
            <p
              className="text-xs sm:text-sm text-white/80 mt-2 tracking-wider"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              タレントマネジメント事業
            </p>
          </div>
        </div>
      </section>

      {/* Content Card */}
      <div className="px-4 md:px-6 lg:px-10 pb-16 md:pb-24">
        <div className="bg-white rounded-2xl p-8 md:p-12 lg:p-16">
          <div className="max-w-3xl mx-auto">
            {/* Description */}
            <div
              className="text-base md:text-lg text-gray-800 leading-[2.2] tracking-wide mb-10"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              <p className="mb-6">
                各種メディア出演、MC、司会など、数多くの実績のあるタレントが所属しております。
              </p>
              <p>
                コンタクトフォームより、お気軽にお問合せください。
              </p>
            </div>

            {/* Activity Gallery */}
            <div className="pt-10 pb-16 border-t border-gray-100">
              <h2
                className="text-xl md:text-2xl font-bold text-gray-900 mb-4 tracking-wide"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                活動の様子
              </h2>
              <p
                className="text-sm md:text-base text-gray-600 leading-[2] mb-10"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                所属タレントの出演・ロケ・イベント等、日々の活動の一場面をご紹介します。
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Large card spanning 2 cols on lg */}
                <div className="relative overflow-hidden rounded-xl group lg:col-span-2 h-[300px] sm:h-[340px]">
                  <img
                    src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/a6f7c28c2fe3768dd60f7d49febd472d.png"
                    alt="映像制作・取材の様子"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <p className="absolute bottom-4 left-5 text-white text-sm md:text-base font-medium tracking-wide" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    映像制作・インタビュー取材
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-xl group h-[300px] sm:h-[340px]">
                  <img
                    src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/59cabcae56308982eb9d66d602c14760.png"
                    alt="イベントMCの様子"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <p className="absolute bottom-4 left-5 text-white text-sm md:text-base font-medium tracking-wide" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    イベントMC
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-xl group h-[300px] sm:h-[340px]">
                  <img
                    src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/300482f856b3faec9b98faff48ca4beb.png"
                    alt="マスコットキャラクターとのイベント出演"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <p className="absolute bottom-4 left-5 text-white text-sm md:text-base font-medium tracking-wide" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    イベント出演
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-xl group h-[300px] sm:h-[340px]">
                  <img
                    src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/bc5fceed0aff8ba214628f60c4796387.png"
                    alt="トークショー出演の様子"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <p className="absolute bottom-4 left-5 text-white text-sm md:text-base font-medium tracking-wide" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    講演・トークショー
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-xl group h-[300px] sm:h-[340px]">
                  <img
                    src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/0709570e8420c772f06086412af481ad.png"
                    alt="ロケ出演の様子"
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <p className="absolute bottom-4 left-5 text-white text-sm md:text-base font-medium tracking-wide" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    ロケ出演
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-12">
              <a
                href="/talent"
                className="inline-flex items-center gap-3 px-14 py-5 bg-[#3D72D9] text-white text-base md:text-lg font-bold rounded-full hover:bg-[#2e5ab5] transition-colors whitespace-nowrap"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                所属タレントはこちらから
                <i className="ri-arrow-right-line text-xl" />
              </a>
            </div>

            {/* Request Section */}
            <div className="py-10 border-t border-gray-100">
              <h2
                className="text-xl md:text-2xl font-bold text-gray-900 mb-10 tracking-wide"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                出演のご依頼
              </h2>

              <div
                className="text-base md:text-lg text-gray-700 leading-[2.2] mb-8"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                <p className="mb-6">
                  弊社タレントが受けられる仕事は下記となります。
                </p>
                <div className="space-y-1 text-base md:text-lg">
                  {requestTypes.map((type) => (
                    <p key={type} className="leading-relaxed">
                      {type}
                    </p>
                  ))}
                </div>
              </div>

              <div
                className="text-base md:text-lg text-gray-700 leading-[2.2]"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                <p>
                  お仕事のご依頼、ご相談はお気軽に
                  <a href="/contact" className="text-gray-900 underline underline-offset-4 hover:text-[#3D72D9] transition-colors">
                    Contact
                  </a>
                  よりお問合せください。
                </p>
                <p className="text-sm md:text-base text-gray-500 mt-4">
                  ※お問い合わせいただいた全ての方にご返信することは出来かねます。予めご了承ください。
                </p>
              </div>
            </div>

            {/* Bottom CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 pt-10 border-t border-gray-100">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 border border-gray-900 text-gray-900 text-sm font-medium rounded-full hover:bg-gray-900 hover:text-white transition-colors whitespace-nowrap"
              >
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}